/**
 * Firebase Analytics イベント辞書（SSOT: 2026-08-13_イベント計測実装依頼書 + データチーム修正）。
 * ここに無いイベント名・パラメータ名・enum値は追加禁止（追加はデータチームへ連絡）。
 *
 * 型ルール（依頼書 §1 ルール4）:
 * - ID系は string（数値でも文字列化）
 * - 金額・秒数・件数は int（number）
 * - 真偽値は 0/1 の int（boolean 禁止）
 * - enum は小文字 snake_case の string
 */

/** パラメータに使える型。boolean はコンパイルエラーになる */
export type FirebaseParamValue = string | number;

export type FirebaseEventParams = Record<string, FirebaseParamValue>;

export type FirebaseCallType = "voice" | "video";

export type FirebaseCallResult =
  | "answered"
  | "rejected"
  | "no_answer"
  | "busy"
  | "unavailable"
  | "error";

export type FirebaseCallEndReason =
  | "hangup_self"
  | "hangup_partner"
  | "pt_exhausted"
  | "network"
  | "timeout";

export type FirebaseBroadcastEndReason =
  | "self"
  | "network"
  | "crash"
  | "banned"
  | "timeout"
  | "connect_failed";

export type FirebaseBroadcastExitReason =
  | "self"
  | "broadcast_ended"
  | "background"
  | "network"
  | "pt_exhausted";

export type FirebaseHeartbeatState =
  | "waiting"
  | "broadcasting"
  | "calling"
  | "viewing";

export type FirebaseAvailabilityState = "offline" | "online" | "available";

export type FirebasePaywallTrigger = "pt_zero" | "self" | "campaign";

/** 課金画面の決済タブ（実UIの3タブ。PAY_TABS と対応） */
export type FirebasePayTab = "iap" | "web" | "paidy";

/**
 * 課金導線が発生したシーン（pt_zero_reached.last_action と同一語彙）。
 * ライブ配信中/通話中に自発的に開いたチャージシート由来の課金を、通常の
 * /purchase ページ由来と区別するために paywall_view / purchase_plan_tap に付与する。
 * 未指定 = 通常の /purchase ページから。
 */
export type FirebasePurchaseScene =
  | "videocall"
  | "voicecall"
  | "live"
  | "chat"
  | "gift"
  | "media_image"
  | "media_video"
  | "media_audio";

/**
 * A/Bテストの群。割り付けは BE（`enroll_*_experiment`）が user_id から決定的に決める。
 * 語彙は BE の到達ログ（`logdb.*_experiment_enrollment` の variant）と揃えること。
 */
export type FirebaseAbVariant = "control" | "treatment";

export type FirebaseLovenseControlType = "toy" | "full";

export type FirebaseBustSize =
  | "a_cup"
  | "b_cup"
  | "c_cup"
  | "d_cup"
  | "e_cup"
  | "big_breasts";

/**
 * 画面ID一覧（依頼書 §4 + 修正: おすすめ=recommend / 探す=find / 検索機能=search）。
 * この値以外を screen_view に入れない。クラス名・ルートパスは禁止。
 */
export const FIREBASE_SCREEN_ID = {
  RECOMMEND: "recommend",
  FIND: "find",
  SEARCH: "search",
  BOARD: "board",
  FOOTSTEP: "footstep",
  RANKING: "ranking",
  GALLERY: "gallery",
  FAVORITE_LIST: "favorite_list",
  LIKE_LIST: "like_list",
  CALL_HISTORY: "call_history",
  FLEAMARKET: "fleamarket",
  PROFILE_SELF: "profile_self",
  CHAT: "chat",
  PAYWALL: "paywall",
  WAITING: "waiting",
} as const;

export type FirebaseScreenId =
  (typeof FIREBASE_SCREEN_ID)[keyof typeof FIREBASE_SCREEN_ID];

/**
 * イベント名 → パラメータ型のマップ。source は共通モジュールが自動付与するため含めない。
 * （screen_view はここに含めず trackFirebaseScreenView 専用）
 */
export type FirebaseEventParamsMap = {
  // §9 その他
  signup_start: { method: string };
  signup_complete: { method: string };
  profile_edit: { field: "about_me" | "age" | "avatar" };
  profile_view: { partner_id: string };
  // プロフ画像タップで拡大表示した操作。配信視聴/通話中プロフィール等、複数画面から
  // 共通コンポーネント経由で発火するため画面種別は区別しない
  profile_avatar_tap: { partner_id: string };
  // 修正#3: conversation_id は廃止、partner_id のみ
  chat_open: { partner_id: string };
  search_execute: {
    /** 使った検索条件をアルファベット順・パイプ区切りで（例: "age|body|bust|videocall"） */
    filter_types: string;
    filter_count: number;
    result_count: number;
    /** バストサイズ指定時のみ付与するカップ数（noPreference時は付与しない） */
    bust_size?: FirebaseBustSize;
  };
  request_sent: {
    request_type: "voicecall" | "videocall" | "image" | "video";
    partner_id: string;
  };
  lovense_sent:
    | {
        surface: "call";
        control_type: FirebaseLovenseControlType;
        partner_id: string;
        call_id: string;
      }
    | {
        surface: "stream";
        control_type: FirebaseLovenseControlType;
        partner_id: string;
        broadcast_id: string;
      };
  // full_control を除く通常メニューが「未実行→実行中」に移った瞬間（購入者端末バイブ発火と同時）。
  // stream では自分・他人問わず誰かの実行開始で発火する
  lovense_execution_start:
    | {
        surface: "call";
        partner_id: string;
        call_id: string;
        duration_sec: number;
      }
    | {
        surface: "stream";
        partner_id: string;
        broadcast_id: string;
        duration_sec: number;
      };
  media_unlock_tap: {
    media_type: "image" | "video";
    partner_id: string;
    price_pt: number;
  };
  gift_select_tap: {
    gift_type: "gift" | "sticker";
    item_id: string;
    price_pt: number;
    partner_id: string;
  };
  reaction_add: {
    reaction_type: "like" | "favorite";
    object_type: "user" | "item";
    object_id: string;
  };
  reaction_remove: {
    reaction_type: "like" | "favorite";
    object_type: "user" | "item";
    object_id: string;
  };
  item_view: { item_id: string; seller_id: string };
  item_purchase_tap: { item_id: string; seller_id: string; price_yen: number };
  post_create: { post_type: "normal" | "onetap" };
  // 修正#2: noti_type 整備までは "noti_type_<値>" 形式（例: "noti_type_3"）
  push_open: { campaign_id: string };
  review_prompt_tap: { trigger: string };

  // §5 通話（call_id は Agora の channel 名をそのまま入れる）
  call_tap: {
    call_id: string;
    call_type: FirebaseCallType;
    partner_id: string;
  };
  call_result:
    | {
        call_id: string;
        call_type: FirebaseCallType;
        partner_id: string;
        result: Exclude<FirebaseCallResult, "error">;
        latency_sec: number;
      }
    | {
        call_id: string;
        call_type: FirebaseCallType;
        partner_id: string;
        result: "error";
        latency_sec: number;
        /** ストア/SDKが返す生のエラーコード（加工禁止） */
        error_code: string;
      };
  call_ended: {
    call_id: string;
    call_type: FirebaseCallType;
    partner_id: string;
    duration_sec: number;
    end_reason: FirebaseCallEndReason;
    ended_by: "self" | "partner";
  };
  call_incoming_view: {
    call_id: string;
    call_type: FirebaseCallType;
    partner_id: string;
  };

  // §6 ライブ配信（broadcast_id は Agora の channel 名。配信者/視聴者で同じ値になること）
  broadcast_start: { broadcast_id: string };
  broadcast_connected: { broadcast_id: string; latency_sec: number };
  broadcast_end: {
    broadcast_id: string;
    duration_sec: number;
    peak_audience_count: number;
    total_audience_count: number;
    end_reason: FirebaseBroadcastEndReason;
  };
  broadcast_enter: { broadcast_id: string; broadcaster_id: string };
  broadcast_exit: {
    broadcast_id: string;
    broadcaster_id: string;
    watch_sec: number;
    exit_reason: FirebaseBroadcastExitReason;
  };
  broadcast_comment_sent: {
    broadcast_id: string;
    broadcaster_id: string;
    char_count: number;
  };

  // §7 稼働状態
  state_heartbeat:
    | { state: "calling"; call_id: string }
    | { state: "broadcasting" | "viewing"; broadcast_id: string }
    | { state: "waiting" };
  availability_toggle: { new_state: FirebaseAvailabilityState };

  // §8 課金ファネル
  // 課金の開始/成功/失敗はBEの決済ログ（log_point）が正のため計測しない。
  // タブ別の離脱率・離脱プランは paywall_tab_view / purchase_plan_tap と
  // BEの成功ログ（log_web_payment_point / transaction_log）の user_id×時刻近接突合で算出する
  pt_zero_reached: { last_action: string };
  /**
   * scene: チャージシートがライブ配信中/通話中/チャット中等に開かれた場合のシーン。
   * 未指定 = 通常の /purchase ページから（trigger="self"時のみ意味を持つ。
   * trigger="pt_zero"時は直前の pt_zero_reached.last_action と同値になる）。
   */
  paywall_view: {
    trigger: FirebasePaywallTrigger;
    scene?: FirebasePurchaseScene;
  };
  /** 課金画面の初期表示（アクティブタブで1回）＋タブ切替時に1回。同値への再セットでは送らない */
  paywall_tab_view: { tab: FirebasePayTab };
  /**
   * タブ内のプランカードをタップした瞬間。PurchasePageTemplate / InsufficientPointSheet /
   * PurchaseQuickChargeSheet の3導線すべてから送信する（確認シート経由の購入も含む）。
   * 金額(円)は含めない。fallbackPriceJpyはIAPではストア実価格とズレうる表示用の固定値で、
   * 正確な金額が必要なら product_id からマスタで引ける。
   * scene: paywall_view と同義（PurchasePageTemplate 由来なら未指定）。
   */
  purchase_plan_tap: {
    tab: FirebasePayTab;
    product_id: string;
    price_pt: number;
    /** 何回目購入ボーナスのカードか。0=通常パッケージ、1〜5=対象のボーナス回数 */
    bonus_level: number;
    scene?: FirebasePurchaseScene;
  };

  // §10 高評価ユーザー紹介オンボーディング（2026-09-03追加。データチーム承認済み）
  /** オンボーディング無料デモ通話の着信バナーの状態遷移 */
  demo_call_banner_step: { step: "show" | "answer" | "decline" };
  /**
   * さがす画面→プロフィール→通話リクエストのオンボーディング進捗。
   * 「開始した人のうち何人が通話まで到達したか」は本イベントと call_result 等の
   * 通常の通話イベントを user_pseudo_id×時刻近接で突合して算出する
   * （Repro と異なり BigQuery エクスポートで後から結合できるため、専用のマーカー
   * イベントは追加しない）。
   */
  high_review_onboarding_step: {
    step: "start" | "reach_profile" | "skip" | "complete";
    /**
     * スキップUIのA/Bテスト群（2026-09-22追加）。実際に描画した群を載せる。
     * control = 現行UI（吹き出し右上・下線付き）、treatment = 控えめUI（吹き出し下部）。
     * 割り付けは BE の `enroll_high_review_onboarding_experiment` 頼みで非同期なため、
     * 確定前に送る step（主に start）では付かない。群別の分母は BE の到達ログを正本にする。
     */
    ab_variant?: FirebaseAbVariant;
  };

  // §11 チャット pt不足の先回りモーダル（2026-09-15追加）
  /**
   * 残高不足のまま次を送ろうとしないと課金導線が届かない層に、滞在中に先回りで出す
   * モーダルの状態遷移。show はモーダル自体の表示で、paywall_view は立たない
   * （charge_tap でチャージシートを開いた時点で従来どおり paywall_view / pt_zero_reached が立つ）。
   */
  /**
   * ビデオ通話が pt 不足で自動終了した直後に、汎用ポイントストアの代わりに出す
   * 文脈モーダルの状態遷移。
   *
   * このモーダルは従来の pt不足シートを**置き換える**もので、露出そのものは
   * 変えない。そのため pt_zero_reached / paywall_view は従来どおり同じ条件で
   * 立て続ける（露出を固定したまま「見せ方」の効果だけを分離して測るため）。
   */
  call_pt_shortage_prompt_step: {
    step: "show" | "charge_tap" | "dismiss";
    /**
     * モーダルを出したきっかけ。通話が切れた瞬間に出たのか、そのあと同じ相手を
     * 探しに来た瞬間に出たのかで刺さる文脈が違うため内訳を取る。
     * show だけでなく charge_tap / dismiss にも同じ値を載せるので、入口ごとの
     * CTA タップ率を show と突合せずに直接割れる。
     */
    trigger: "call_end" | "call_retap" | "chat_revisit" | "profile_revisit";
    /** 同一相手へのかけ直しを追うため。この施策の根拠がそこにある */
    partner_id: string;
    /**
     * 通話終了の詳細コード。Phase 1 は 201（pt 不足による自動終了）限定なので、
     * それ以外で発火していないかの検証に使う。100（自分で切った）に誤爆すると
     * 露出が変わって効果測定が壊れるため、ここは必ず載せる。
     */
    call_end_detail: number;
    /** Phase 1 は video 限定。将来 voice に広げたときに割れるよう最初から載せる */
    call_type: "video" | "voice";
    /** 表示時点の残高(pt)。「次の1分に足りない」判定が意図どおりかの検証用 */
    balance_pt?: number;
    /** 通話の継続秒数。文脈が育つ前に切れたケースを切り分ける */
    call_duration_sec?: number;
  };

  chat_pt_shortage_prompt_step: {
    step: "show" | "charge_tap" | "dismiss";
    /**
     * モーダルを出したきっかけ。チャット滞在で出たのか、相手プロフィールへ回って
     * から出たのか、いいねで出たのかで刺さる文脈が違うため内訳を取る。
     * show だけでなく charge_tap / dismiss にも同じ値を載せるので、入口ごとの
     * CTA タップ率を show と突合せずに直接割れる。
     */
    trigger?: "chat_dwell" | "profile_dwell" | "favorite";
    partner_id: string;
  };

  /**
   * 好みタイプ選択オンボーディングのファネル。登録直後に1回だけ通る画面。
   *
   * 段は効果測定ダッシュボード（jambo-ds-effect-dashboard の
   * `src/lib/prefOnboarding.ts` の PREF_ONBOARDING_STEPS）と1:1。**片方だけ足すと
   * 表に出ない段ができる。**
   *
   * - show: 画面が出た（母数。ここを分母に残存率を見る）
   * - search: 「この条件でさがす」を押した
   * - result: 候補が返って一覧が出た
   * - result_empty: 候補が0件だった（result とは排他）
   * - search_error: 検索に失敗した
   * - complete: 「アプリをはじめる」まで到達した（完走）
   *
   * ★途中でアプリを閉じた人は次の段が出ないだけで、離脱を表す専用イベントは持たない。
   * ★スキップは段として持たない。show からの脱落として読む
   *   （スキップ導線が複数の画面にあり、どこで押したかは段の欠落で分かる）。
   */
  preference_onboarding_step: {
    step:
      | "show"
      | "search"
      | "result"
      | "result_empty"
      | "search_error"
      | "complete";
  };
};

export type FirebaseEventName = keyof FirebaseEventParamsMap;

/**
 * イベント名の定数。呼び出し側は文字列リテラルを直書きせず必ずこれを使う。
 * FirebaseEventParamsMap のキーと1:1（片方だけ更新すると型エラーになる）。
 */
export const FIREBASE_EVENT_NAME: {
  [K in FirebaseEventName as Uppercase<K>]: K;
} = {
  SIGNUP_START: "signup_start",
  SIGNUP_COMPLETE: "signup_complete",
  PROFILE_EDIT: "profile_edit",
  PROFILE_VIEW: "profile_view",
  PROFILE_AVATAR_TAP: "profile_avatar_tap",
  CHAT_OPEN: "chat_open",
  SEARCH_EXECUTE: "search_execute",
  REQUEST_SENT: "request_sent",
  LOVENSE_SENT: "lovense_sent",
  LOVENSE_EXECUTION_START: "lovense_execution_start",
  MEDIA_UNLOCK_TAP: "media_unlock_tap",
  GIFT_SELECT_TAP: "gift_select_tap",
  REACTION_ADD: "reaction_add",
  REACTION_REMOVE: "reaction_remove",
  ITEM_VIEW: "item_view",
  ITEM_PURCHASE_TAP: "item_purchase_tap",
  POST_CREATE: "post_create",
  PUSH_OPEN: "push_open",
  REVIEW_PROMPT_TAP: "review_prompt_tap",
  CALL_TAP: "call_tap",
  CALL_RESULT: "call_result",
  CALL_ENDED: "call_ended",
  CALL_INCOMING_VIEW: "call_incoming_view",
  BROADCAST_START: "broadcast_start",
  BROADCAST_CONNECTED: "broadcast_connected",
  BROADCAST_END: "broadcast_end",
  BROADCAST_ENTER: "broadcast_enter",
  BROADCAST_EXIT: "broadcast_exit",
  BROADCAST_COMMENT_SENT: "broadcast_comment_sent",
  STATE_HEARTBEAT: "state_heartbeat",
  AVAILABILITY_TOGGLE: "availability_toggle",
  PT_ZERO_REACHED: "pt_zero_reached",
  PAYWALL_VIEW: "paywall_view",
  PAYWALL_TAB_VIEW: "paywall_tab_view",
  PURCHASE_PLAN_TAP: "purchase_plan_tap",
  DEMO_CALL_BANNER_STEP: "demo_call_banner_step",
  HIGH_REVIEW_ONBOARDING_STEP: "high_review_onboarding_step",
  CHAT_PT_SHORTAGE_PROMPT_STEP: "chat_pt_shortage_prompt_step",
  CALL_PT_SHORTAGE_PROMPT_STEP: "call_pt_shortage_prompt_step",
  PREFERENCE_ONBOARDING_STEP: "preference_onboarding_step",
};
